/**
 * The filter bar for /tickets and /review — the search box, and what is
 * currently switched on.
 *
 * =========================================================================
 * WHY IT SHRANK (P30, Roman's point 3)
 * =========================================================================
 * Until P30 this bar held six chip groups: status, company, language, owner,
 * review-reason family and a created range — about thirty controls above a
 * table that could show fifty rows. Every one of them named a COLUMN, and the
 * column itself was silent about whether it was being filtered. So the bar had
 * to be read to understand the table.
 *
 * The controls moved into the header menus, where the column they filter is.
 * What is left here is the two things that belong to the whole board rather
 * than to one column: the free-text search (which reaches five fields at once),
 * and a plain statement of what is currently on — one removable chip per active
 * filter, plus "Clear N".
 *
 * =========================================================================
 * THE SEARCH BOX TAKES SEVERAL TERMS, AND REACHES THE NAMES (P32)
 * =========================================================================
 * Katia asked for "alle Anfragen von BASWA für die ACTICIDE Produkte", which is
 * two terms and one of them a resolved ERP name. Both now work: terms are
 * AND-ed, `"..."` holds a phrase together, and the name is matched by the
 * SERVER inside its own WHERE — see `api/src/functions/tickets/list.ts`.
 *
 * P22's omission is answered rather than reversed. The rule it protected was
 * that a name must not be filtered AFTER the query has truncated its rows; the
 * fix was to move the comparison into the query, not to allow the post-filter.
 * The Customer and Product COLUMN filters are still client-side and still say
 * so — they are the way to narrow what is on screen, the box is the way to ask
 * the database a smaller question.
 */

import { useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from './ui/Field';
import {
    ENTITY_STATE_LABELS, NOT_IDENTIFIED, REVIEW_REASON_FAMILY_LABELS, TICKET_COLUMN_LABELS,
    TICKET_STATUS_LABELS, labelFor, type ReviewReasonFamily,
} from '../lib/labels';
import type { ColumnFilterState } from '../hooks/useColumnFilters';
import { UNASSIGNED, type MultiFilterKey, type TicketFilters } from '../hooks/useTicketFilters';
import { COMPANIES } from './TicketTable';

/** One active filter, as the reader sees it and as it is taken off again. */
export interface ActiveChip {
    id: string;
    /** The column the filter belongs to — so a chip reads "Status: Needs review". */
    column: string;
    value: string;
    remove: () => void;
}

/**
 * Every switched-on filter, in column order, each with the click that removes
 * exactly it. Exported because the chips are the only remaining evidence of the
 * server filters on screen, and a test that they match the URL is worth having.
 */
export function activeChips(
    filters: TicketFilters,
    toggleIn: (key: MultiFilterKey, value: string) => void,
    setOne: <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => void,
): ActiveChip[] {
    const chips: ActiveChip[] = [];
    const list = (key: MultiFilterKey, column: string, label: (v: string) => string) => {
        for (const v of filters[key]) {
            chips.push({ id: `${key}:${v}`, column: labelFor(TICKET_COLUMN_LABELS, column), value: label(v), remove: () => toggleIn(key, v) });
        }
    };

    list('status', 'status', v => labelFor(TICKET_STATUS_LABELS, v));
    list('reviewReasonFamily', 'reviewReason', v => REVIEW_REASON_FAMILY_LABELS[v as ReviewReasonFamily]?.label ?? v);
    list('companyCode', 'companyCode', v => COMPANIES.find(c => c.code === v)?.label ?? v);
    list('requestedLanguage', 'requestedLanguage', v => v);
    list('assignedTo', 'assignedTo', v => (v === UNASSIGNED ? 'Nobody' : v));

    if (filters.createdFrom) {
        chips.push({
            id: 'createdFrom', column: labelFor(TICKET_COLUMN_LABELS, 'createdAt'), value: `from ${filters.createdFrom}`,
            remove: () => setOne('createdFrom', null),
        });
    }
    if (filters.createdTo) {
        chips.push({
            id: 'createdTo', column: labelFor(TICKET_COLUMN_LABELS, 'createdAt'), value: `to ${filters.createdTo}`,
            remove: () => setOne('createdTo', null),
        });
    }
    return chips;
}

/**
 * The client-side column filters as chips. Separate from `activeChips` because
 * they are a different mechanism (they see the loaded rows), and the chip has
 * to say the column so a reader can tell which of the two a filter is.
 *
 * A long value list is summarised rather than printed: the chip states what is
 * on, and the column's own menu is where it is read in full.
 */
export function columnFilterChips(
    columnFilters: Record<string, ColumnFilterState>,
    setColumnFilter: (key: string, state: ColumnFilterState | null) => void,
): ActiveChip[] {
    const readable = (v: string) => {
        if (v === NOT_IDENTIFIED) return `(${ENTITY_STATE_LABELS.notIdentified.label.toLowerCase()})`;
        return v === '' ? '(empty)' : v;
    };
    return Object.entries(columnFilters).map(([key, state]) => {
        const parts: string[] = [];
        if (state.selected) {
            const values = [...state.selected];
            parts.push(values.length === 0
                ? 'nothing selected'
                : values.slice(0, 2).map(readable).join(', ') + (values.length > 2 ? ` +${values.length - 2}` : ''));
        }
        if (state.min != null) parts.push(`≥ ${state.min}`);
        if (state.max != null) parts.push(`≤ ${state.max}`);
        return {
            id: `column:${key}`,
            column: labelFor(TICKET_COLUMN_LABELS, key),
            value: parts.join(' · '),
            remove: () => setColumnFilter(key, null),
        };
    });
}

export default function TicketFilterBar({
    filters, toggleIn, setOne, clear, activeCount, extraChips,
}: {
    filters: TicketFilters;
    toggleIn: (key: MultiFilterKey, value: string) => void;
    setOne: <K extends keyof TicketFilters>(key: K, value: TicketFilters[K]) => void;
    clear: () => void;
    activeCount: number;
    /** The client-side column filters, which the board owns rather than this bar. */
    extraChips: ActiveChip[];
}) {
    // The search box is the one control that must NOT hit the server on every
    // keystroke — it is typed, not clicked. Local while typing, pushed to the
    // URL 350 ms after the reader stops.
    const [searchDraft, setSearchDraft] = useState(filters.search ?? '');
    useEffect(() => { setSearchDraft(filters.search ?? ''); }, [filters.search]);
    useEffect(() => {
        const current = filters.search ?? '';
        if (searchDraft === current) return;
        const t = setTimeout(() => setOne('search', searchDraft.trim() || null), 350);
        return () => clearTimeout(t);
    }, [searchDraft, filters.search, setOne]);

    const chips = [...activeChips(filters, toggleIn, setOne), ...extraChips];

    return (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
            <div className="flex flex-wrap items-center gap-2">
                <Search className="h-4 w-4 text-muted" />
                {/* P40: the last control on either board without the house ring. The
                    kit's `Input` carries it and the house height; `aria-label` because
                    the magnifier beside it is decorative and named nothing. */}
                <Input
                    type="search"
                    aria-label="Search tickets"
                    value={searchDraft}
                    onChange={e => setSearchDraft(e.target.value)}
                    placeholder='Search — several terms narrow, e.g. BASWA ACTICIDE'
                    title={'Every term must match, so "BASWA ACTICIDE" finds the ACTICIDE requests of BASWA. Put a name in double quotes to keep it together ("Karl Bubenhofer"). Searched: the ticket number, the customer and product text as the mail wrote them, the resolved customer and product names from the ERP, the ERP ids and the review reason. Punctuation is ignored in the names, so "Duna Corradini" finds DUNA-CORRADINI S.P.A.'}
                    className="min-w-[280px] flex-1"
                />
                {activeCount > 0 && (
                    <button
                        type="button"
                        onClick={clear}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                    >
                        <X className="h-3 w-3" /> Clear {activeCount} filter{activeCount === 1 ? '' : 's'}
                    </button>
                )}
            </div>

            {chips.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {/* P38, task 6: 11 px -> 12 px. `tracking-wide` stays; the words are
                        three and the row wraps, so it costs no layout. */}
                    <span className="text-xs font-medium uppercase tracking-wide text-muted">Filtered by</span>
                    {chips.map(c => (
                        <button
                            key={c.id}
                            type="button"
                            onClick={c.remove}
                            title={`Remove this filter (${c.column})`}
                            // P38: §2's `--info` family instead of stock blue. The chip is a
                            // statement by the agent about what the reader asked for, which is
                            // what info means; nothing here needs amber.
                            className="inline-flex items-center gap-1 rounded-full border border-info-100 bg-info-50 px-2.5 py-1 text-xs font-medium text-info hover:bg-info-100"
                        >
                            <span className="opacity-75">{c.column}:</span> {c.value}
                            <X className="h-3 w-3" />
                        </button>
                    ))}
                </div>
            )}
            {activeCount === 0 && (
                <p className="mt-2 text-xs text-muted">
                    No filters. Every column header carries a funnel menu — Status, Company, Lang, Owner and Created filter on the
                    server; the rest filter the rows that are loaded.
                </p>
            )}
        </div>
    );
}
